import {
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
} from 'downshift';
import React from 'react';
import styled, {css, keyframes} from 'styled-components';
import {exists} from '../../../utils/exists';
import {
  backgroundColor,
  bordersColor,
  complementaryColor,
  darkGrayColor,
  notificationErrorColor,
  primaryColor,
} from '../../constants/colors';
import {ElementLayers} from '../../constants/layers';
import {fsXS, fwMediumBold} from '../../constants/typography';
import Icon, {IconSize} from '../../Icon/Icon';
import {GenericIconName} from '../../Icon/IconName';
import {
  inputElementBorderRadius,
  inputElementHeight,
  inputElementPadding,
} from './constants';
import {InfoText} from './InputStatus';

import BottomSheet from '../../BottomSheet/BottomSheet';

const DropDownArrowIcon = styled(Icon)`
  fill: ${darkGrayColor};
  position: absolute;
  right: ${inputElementPadding}px;
`;

const ButtonText = styled.span`
  font-size: ${fsXS}px;
  ${fwMediumBold};
  color: ${darkGrayColor};
`;

const BottomSheetMenu = styled.ul`
  height: 90vh;
  padding-left: 10px;
  padding-right: 10px;
`;

const dropDownButtonActiveStyle = css`
  border-color: ${primaryColor};

  ${DropDownArrowIcon} {
    fill: ${primaryColor};
  }
  ${ButtonText} {
    color: ${primaryColor};
  }
`;

export const DropDownButton = styled.button<{
  active: boolean;
  hasError: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  cursor: pointer;
  height: ${inputElementHeight}px;
  border-radius: ${inputElementBorderRadius}px;
  background-color: ${complementaryColor};
  border: 1px solid ${bordersColor};
  padding: ${inputElementPadding}px;

  &:hover,
  &:focus {
    ${dropDownButtonActiveStyle};
  }

  ${props =>
    props.hasError &&
    css`
      border-color: ${notificationErrorColor};
      &:hover,
      &:focus {
        border-color: ${notificationErrorColor};
      }
    `}

  ${props => props.active && dropDownButtonActiveStyle};
`;

const growAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px)
  }

  to {
    opacity: 1;
    transform: translateY(0px)
  }
`;

const DropdownBox = styled.div<{longList: boolean; visible: boolean}>`
  position: absolute;
  min-width: 100%;
  max-height: 160px;
  margin-top: 10px;
  animation: ${growAnimation} 200ms linear;
  box-shadow: 0 10px 15px 0 rgba(0, 0, 0, 0.08), 0 2px 2px 0 rgba(0, 0, 0, 0.05),
    0 -2px 2px 0 rgba(0, 0, 0, 0.02);
  border: none;
  border-radius: 2px;
  z-index: ${ElementLayers.modal};
  background-color: ${complementaryColor};
  visibility: hidden;
  ${props =>
    props.longList
      ? css`
          height: 172px;
        `
      : css`
          height: 190px;
        `}
  ${props =>
    props.visible &&
    css`
      visibility: visible;
    `}
`;

const DropDownList = styled.ul<{longList: boolean}>`
  border: none;
  border-radius: 2px;
  overflow: auto;
  box-sizing: border-box;
  height: 100%;
  margin: 0;
  padding-left: 0px;

  &:focus {
    outline: none;
  }

  ${props =>
    props.longList &&
    css`
      overflow: auto;
      padding-bottom: 10px;
      mask-image: linear-gradient(
        to bottom,
        black calc(100% - 30px),
        transparent 100%
      );
    `}
`;

const Option = styled.li<{highlighted: boolean}>`
  height: 32px;
  background-color: #ffffff;
  margin-top: 5px;
  display: flex;
  align-items: center;

  font-size: ${fsXS}px;
  ${fwMediumBold};
  line-height: 1.43;
  color: ${primaryColor};
  padding-left: ${inputElementPadding}px;
  cursor: pointer;

  &:hover {
    background-color: ${backgroundColor};
  }
  ${props =>
    props.highlighted &&
    css`
      background-color: ${backgroundColor};
    `}
`;

const Wrapper = styled.div`
  position: relative;
`;

export interface DropDownItem<T> {
  label: string | React.ReactNode;
  value: T;
}

export interface BottomSheetAdditionalProps {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  subHeader?: React.ReactNode;
  afterClose?: () => void;
}

interface Props<T> {
  items: Array<DropDownItem<T>>;
  label: string;
  value?: DropDownItem<T>;
  onChange?: (value: DropDownItem<T>) => void;
  stateReducerOverWrite?: (
    state: UseSelectState<DropDownItem<T>>,
    changes: UseSelectStateChangeOptions<DropDownItem<T>>,
  ) => Partial<UseSelectState<DropDownItem<T>>>;
  className?: string;
  id?: string;
  errorMessage?: string;
  helpText?: string;
  withBottomSheet?: boolean;
  bottomSheetAdditionalProps?: BottomSheetAdditionalProps;
  searchBar?: React.ReactNode;
}

function DropdownSelect<T>({
  items,
  label,
  value,
  onChange,
  stateReducerOverWrite,
  className,
  id,
  errorMessage,
  helpText,
  withBottomSheet,
  bottomSheetAdditionalProps,
  searchBar,
}: Props<T>) {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    getLabelProps,
  } = useSelect({
    items,
    selectedItem: value,
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        onChange?.(changes.selectedItem);
      }
    },
    ...(!!stateReducerOverWrite && {stateReducer: stateReducerOverWrite}),
    id,
  });
  const hasError = exists(errorMessage);
  const infoText = errorMessage ?? helpText;

  const isLongList = items.length > 5;

  const {onClick: onToggleClick, ...toggleButtonProps} = getToggleButtonProps();

  const dropdownContent =
    isOpen &&
    items.map((item, index) => (
      <Option
        highlighted={highlightedIndex === index}
        key={`${item.value}${index}`}
        {...getItemProps({item, index})}
      >
        {item.label}
      </Option>
    ));

  const isLabelValid = () =>
    typeof selectedItem?.label === 'string' ||
    React.isValidElement(selectedItem?.label);

  return (
    <Wrapper className={className}>
      <label {...getLabelProps()}>
        <DropDownButton
          {...toggleButtonProps}
          hasError={hasError}
          onClick={e => {
            e.preventDefault();
            onToggleClick(e);
          }}
          active={isOpen || exists(selectedItem)}
        >
          {selectedItem && isLabelValid() ? (
            <ButtonText>{selectedItem.label}</ButtonText>
          ) : (
            <ButtonText>{label}</ButtonText>
          )}
          <DropDownArrowIcon
            asset={
              isOpen ? GenericIconName.IcDropdownUp : GenericIconName.IcDropdown
            }
            size={IconSize.XS}
          />
        </DropDownButton>
      </label>
      {withBottomSheet ? (
        <BottomSheet active={isOpen} {...bottomSheetAdditionalProps}>
          <BottomSheetMenu {...getMenuProps()}>
            {dropdownContent}
          </BottomSheetMenu>
        </BottomSheet>
      ) : (
        <DropdownBox visible={isOpen} longList={isLongList}>
          {searchBar}
          <DropDownList longList={isLongList} {...getMenuProps()}>
            {dropdownContent}
          </DropDownList>
        </DropdownBox>
      )}
      {infoText && <InfoText isError={hasError}>{infoText}</InfoText>}
    </Wrapper>
  );
}

export default DropdownSelect;
